/**
 * Deterministic chip palette for categorical / multi-select cells.
 *
 * Same string → same color across the whole app. The palette is hand-tuned
 * to look at home next to the brand colours without competing with them
 * (Airtable-style soft pastels in light mode, slightly more saturated tones
 * in dark so they remain legible on a dark canvas).
 */

export interface ChipColor {
  bg: string;
  fg: string;
  border: string;
}

// 16 distinct hues, evenly distributed around the wheel. Index 0 (slate) is
// reserved for unknown / fallback so deterministic hashes never produce a
// colour that conflicts with the brand primary.
const LIGHT_PALETTE: ChipColor[] = [
  { bg: '#EEF1F5', fg: '#475569', border: '#CBD5E1' }, // slate
  { bg: '#FEE2E2', fg: '#B91C1C', border: '#FCA5A5' }, // red
  { bg: '#FED7AA', fg: '#9A3412', border: '#FDBA74' }, // orange
  { bg: '#FEF3C7', fg: '#92400E', border: '#FCD34D' }, // amber
  { bg: '#FEF9C3', fg: '#854D0E', border: '#FDE047' }, // yellow
  { bg: '#ECFCCB', fg: '#3F6212', border: '#BEF264' }, // lime
  { bg: '#DCFCE7', fg: '#166534', border: '#86EFAC' }, // green
  { bg: '#D1FAE5', fg: '#065F46', border: '#6EE7B7' }, // emerald
  { bg: '#CCFBF1', fg: '#115E59', border: '#5EEAD4' }, // teal
  { bg: '#CFFAFE', fg: '#155E75', border: '#67E8F9' }, // cyan
  { bg: '#DBEAFE', fg: '#1E40AF', border: '#93C5FD' }, // blue
  { bg: '#E0E7FF', fg: '#3730A3', border: '#A5B4FC' }, // indigo
  { bg: '#EDE9FE', fg: '#5B21B6', border: '#C4B5FD' }, // violet
  { bg: '#F3E8FF', fg: '#6B21A8', border: '#D8B4FE' }, // purple
  { bg: '#FAE8FF', fg: '#86198F', border: '#F0ABFC' }, // fuchsia
  { bg: '#FCE7F3', fg: '#9D174D', border: '#F9A8D4' }, // pink
];

const DARK_PALETTE: ChipColor[] = [
  { bg: 'rgba(148,163,184,0.16)', fg: '#CBD5E1', border: 'rgba(148,163,184,0.30)' },
  { bg: 'rgba(248,113,113,0.16)', fg: '#FCA5A5', border: 'rgba(248,113,113,0.30)' },
  { bg: 'rgba(251,146,60,0.16)',  fg: '#FDBA74', border: 'rgba(251,146,60,0.30)'  },
  { bg: 'rgba(251,191,36,0.16)',  fg: '#FCD34D', border: 'rgba(251,191,36,0.30)'  },
  { bg: 'rgba(250,204,21,0.16)',  fg: '#FDE047', border: 'rgba(250,204,21,0.30)'  },
  { bg: 'rgba(163,230,53,0.16)',  fg: '#BEF264', border: 'rgba(163,230,53,0.30)'  },
  { bg: 'rgba(74,222,128,0.16)',  fg: '#86EFAC', border: 'rgba(74,222,128,0.30)'  },
  { bg: 'rgba(52,211,153,0.16)',  fg: '#6EE7B7', border: 'rgba(52,211,153,0.30)'  },
  { bg: 'rgba(45,212,191,0.16)',  fg: '#5EEAD4', border: 'rgba(45,212,191,0.30)'  },
  { bg: 'rgba(34,211,238,0.16)',  fg: '#67E8F9', border: 'rgba(34,211,238,0.30)'  },
  { bg: 'rgba(96,165,250,0.16)',  fg: '#93C5FD', border: 'rgba(96,165,250,0.30)'  },
  { bg: 'rgba(129,140,248,0.16)', fg: '#A5B4FC', border: 'rgba(129,140,248,0.30)' },
  { bg: 'rgba(167,139,250,0.16)', fg: '#C4B5FD', border: 'rgba(167,139,250,0.30)' },
  { bg: 'rgba(192,132,252,0.16)', fg: '#D8B4FE', border: 'rgba(192,132,252,0.30)' },
  { bg: 'rgba(232,121,249,0.16)', fg: '#F0ABFC', border: 'rgba(232,121,249,0.30)' },
  { bg: 'rgba(244,114,182,0.16)', fg: '#F9A8D4', border: 'rgba(244,114,182,0.30)' },
];

/**
 * FNV-1a 32-bit hash. Stable across runs and platforms.
 */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export const CATEGORY_PALETTE_SIZE = LIGHT_PALETTE.length;

/** Index into the palette for a given string value. Stable across renders. */
export function paletteIndexFor(value: string): number {
  // Skip index 0 (slate / fallback) for hashed colours so brighter hues lead.
  const h = hashString(value || '');
  return 1 + (h % (CATEGORY_PALETTE_SIZE - 1));
}

export function getCategoryColor(index: number, isDark: boolean): ChipColor {
  const palette = isDark ? DARK_PALETTE : LIGHT_PALETTE;
  const safe = ((index % palette.length) + palette.length) % palette.length;
  return palette[safe];
}

/** Convenience: hash a value to a chip colour in one call. */
export function chipColorFor(value: string, isDark: boolean): ChipColor {
  return getCategoryColor(paletteIndexFor(value), isDark);
}
